import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Circle, Loader2, Lock, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { CameraCapture } from "@/components/kyc/CameraCapture";
import { CiecForm, DocDrop } from "@/components/kyc/CorporateInputs";

const ICON = {
  pendiente: <Circle size={16} className="text-white/30" />,
  en_proceso: <Loader2 size={16} className="text-[#FFC629] animate-spin" />,
  aprobado: <CheckCircle2 size={16} className="text-emerald-400" />,
  observado: <AlertTriangle size={16} className="text-amber-400" />,
  rechazado: <XCircle size={16} className="text-rose-400" />,
};
const DECISION = {
  aprobado: { cls: "bg-emerald-500/15 border-emerald-400/40 text-emerald-200", title: "Identidad verificada", icon: <CheckCircle2 size={22} className="text-emerald-400" /> },
  revision_manual: { cls: "bg-amber-500/15 border-amber-400/40 text-amber-100", title: "En revisión de cumplimiento", icon: <AlertTriangle size={22} className="text-amber-400" /> },
  rechazado: { cls: "bg-rose-500/15 border-rose-400/40 text-rose-100", title: "Verificación rechazada", icon: <XCircle size={22} className="text-rose-400" /> },
};
const AUTO_ORDER = ["ine_validation", "curp_renapo", "screening", "decision"];

export function IdentityCheck({ sid, onContinue }) {
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(null);
  const running = useRef(false);
  const base = `/public/onboarding/sessions/${sid}/kyc`;

  useEffect(() => {
    if (!sid) return;
    api.get(base).then(({ data }) => (data.started ? setV(data) : api.post(`${base}/start`).then((r) => setV(r.data)))).catch((e) => toast.error(errMsg(e)));
  }, [sid, base]);

  const upload = async (stepKey, blob, filename) => {
    setBusy(true); setLive(v?.steps.find((s) => s.key === stepKey)?.working || "Procesando…");
    const fd = new FormData(); fd.append("file", blob, filename);
    try { const { data } = await api.post(`${base}/upload/${stepKey}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setV(data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); setLive(null); }
  };
  const submitCiec = async (form) => {
    setBusy(true); setLive(v?.steps.find((s) => s.key === "sat_ciec")?.working);
    try { const { data } = await api.post(`${base}/ciec`, form); setV(data); toast.success("SAT conectado: financieros y flujos listos"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); setLive(null); }
  };

  useEffect(() => {
    if (!v || running.current || busy) return;
    const manualDone = v.steps.filter((s) => !s.auto).every((s) => s.status === "aprobado");
    const nextAuto = AUTO_ORDER.find((k) => ["pendiente", "en_proceso"].includes(v.steps.find((s) => s.key === k)?.status));
    if (!manualDone || !nextAuto || ["aprobado", "rechazado", "revision_manual"].includes(v.status)) return;
    running.current = true;
    const step = v.steps.find((s) => s.key === nextAuto);
    setLive(step.working);
    const t = setTimeout(() => {
      api.post(`${base}/run/${nextAuto}`).then(({ data }) => setV(data)).catch((e) => toast.error(errMsg(e))).finally(() => { running.current = false; setLive(null); });
    }, 900);
    return () => clearTimeout(t);
  }, [v, busy, base]);

  if (!v) return <div className="ink rounded-3xl p-8 text-white/70 text-sm flex items-center gap-2" data-testid="kyc-loading"><Loader2 size={16} className="animate-spin" /> Preparando verificación segura…</div>;

  const current = v.steps.find((s) => s.key === v.next_step && !s.auto);
  const finished = ["aprobado", "rechazado", "revision_manual"].includes(v.status);
  const dec = DECISION[v.status];
  const canContinue = v.status === "aprobado" || v.status === "revision_manual";
  const newPhrase = () => api.post(`${base}/phrase`).then(({ data }) => setV((x) => ({ ...x, phrase: data.phrase }))).catch((e) => toast.error(errMsg(e)));
  const retry = <button data-testid="kyc-retry" onClick={() => api.post(`${base}/reset`).then(({ data }) => setV(data)).catch((e) => toast.error(errMsg(e)))} className="text-sm rounded-full border border-white/25 px-4 py-2 font-semibold hover:bg-white/10">Intentar de nuevo ({v.max_attempts - v.attempt} restantes)</button>;

  return (
    <div className="ink rounded-3xl p-5 sm:p-6 text-white space-y-5 relative overflow-hidden" data-testid="kyc-panel">
      <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#FF7A00]/20 blur-3xl pointer-events-none" />
      <div className="flex items-start justify-between gap-3 relative">
        <div className="flex items-center gap-3"><div className="h-11 w-11 rounded-2xl brand-gradient grid place-items-center text-[#0B0B0B]"><ShieldCheck size={22} /></div><div><h3 className="font-display font-bold text-lg leading-tight">Verificación de identidad</h3><p className="text-xs text-white/60">KYC · PLD · biometría · listas restrictivas</p></div></div>
        <span className="text-[10px] font-bold tracking-widest uppercase rounded-full border border-white/20 px-2.5 py-1 text-white/80 inline-flex items-center gap-1"><Lock size={10} /> Cifrado</span>
      </div>

      <div className="relative">
        <div className="flex items-center justify-between text-xs mb-1.5"><span data-testid="kyc-live" className={`font-semibold ${live ? "text-[#FFC629] animate-pulse" : "text-white/70"}`}>{live || (finished ? dec?.title : current ? `Paso ${v.steps.indexOf(current) + 1} de ${v.steps.length}: ${current.title}` : "Procesando…")}</span><span data-testid="kyc-progress" className="font-display font-bold">{v.progress}%</span></div>
        <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full brand-gradient transition-[width] duration-700 ease-out" style={{ width: `${Math.max(4, v.progress)}%` }} /></div>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-5 relative">
        <ol className="space-y-1.5" data-testid="kyc-steps">
          {v.steps.map((s, i) => (
            <li key={s.key} data-testid={`kyc-step-${s.key}`} data-status={s.status} className={`flex items-start gap-3 rounded-xl px-3 py-2 transition-colors ${s.key === v.next_step ? "bg-white/[.08] ring-1 ring-[#FFC629]/40" : "bg-white/[.03]"}`}>
              <span className="mt-0.5 shrink-0">{ICON[s.status]}</span>
              <div className="min-w-0"><div className="text-sm font-semibold leading-tight">{i + 1}. {s.title}</div><div className={`text-[11px] leading-snug ${s.status === "observado" ? "text-amber-300" : s.status === "rechazado" ? "text-rose-300" : "text-white/55"}`}>{s.message || s.desc}</div></div>
            </li>
          ))}
        </ol>

        <div className="space-y-3">
          {!finished && current && (
            <div className="rounded-2xl bg-white/[.06] border border-white/10 p-4 space-y-3" data-testid="kyc-current">
              <div><div className="text-sm font-bold">{current.title}</div><div className="text-xs text-white/60">{current.desc}</div></div>
              {current.key === "selfie_video" && (
                <div className="rounded-xl bg-[#FFC629]/10 border border-[#FFC629]/40 p-3 text-center" data-testid="kyc-phrase-box"><div className="text-[10px] uppercase tracking-widest text-[#FFC629] font-bold">Di en voz alta</div><div data-testid="kyc-phrase" className="font-display text-2xl font-extrabold text-white tracking-tight mt-1">«{v.phrase}»</div><button onClick={newPhrase} className="text-[11px] text-white/60 underline mt-1">otra frase</button></div>
              )}
              {["document", "ciec"].includes(current.kind) ? (
                current.kind === "ciec" ? <CiecForm onSubmit={submitCiec} busy={busy} defaultRfc={v.rfc_hint} /> : <DocDrop stepKey={current.key} busy={busy} onFile={(f) => upload(current.key, f, f.name)} />
              ) : (
                <CameraCapture stepKey={current.key} mode={current.kind} facing={current.key === "selfie_video" ? "user" : "environment"} busy={busy} onCapture={(blob, name) => upload(current.key, blob, name)} />
              )}
              {busy && <div className="text-xs text-[#FFC629] flex items-center gap-2" data-testid="kyc-busy"><Loader2 size={14} className="animate-spin" /> {live}</div>}
            </div>
          )}
          {!finished && !current && <div className="rounded-2xl bg-white/[.06] border border-white/10 p-6 text-center text-sm text-white/70 flex flex-col items-center gap-2" data-testid="kyc-auto-running"><Loader2 size={22} className="animate-spin text-[#FFC629]" />{live || "Verificando con INE, RENAPO y listas restrictivas…"}<span className="text-[11px] text-white/40">No cierres esta ventana</span></div>}
          {finished && dec && (
            <div className={`rounded-2xl border p-4 space-y-2 ${dec.cls}`} data-testid="kyc-decision" data-status={v.status}>
              <div className="flex items-center gap-2 font-display font-bold text-lg">{dec.icon} {dec.title}</div>
              <div className="text-sm">{v.decision?.summary}</div>
              {v.decision?.reasons?.length > 0 && <ul className="text-xs list-disc pl-5 space-y-0.5 opacity-90">{v.decision.reasons.map((r) => <li key={r}>{r}</li>)}</ul>}
              <div className="text-[11px] opacity-70">Riesgo {v.risk} · folio {v.folio} · {v.status === "revision_manual" ? "un oficial de cumplimiento revisará tu caso; puedes continuar con tus documentos." : v.status === "rechazado" ? "contacta a tu asesor si crees que es un error." : "gracias por tu paciencia."}</div>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 relative">
        <p className="text-[11px] text-white/50 max-w-md">Fotos y videos se guardan cifrados con hash SHA-256; datos sensibles cifrados y con bitácora de acceso. Resguardo 10 años (LFPIORPI art. 18).</p>
        <div className="flex items-center gap-2">
          {finished && v.status === "rechazado" && v.attempt < v.max_attempts && retry}
          {finished && canContinue && <button data-testid="universal-onboarding-step-next" onClick={onContinue} className="btn-brand text-sm">Continuar con mis documentos</button>}
        </div>
      </div>
      <p className="text-[10px] text-white/40">Folio {v.folio} · intento {v.attempt}/{v.max_attempts}</p>
    </div>
  );
}
